import { type ThemeState } from '@atlaskit/tokens';
import { logError } from '@databases/logs';
import {
  isEmbed,
  isMobile,
  isStandalone,
  isNativeEmbed,
  getAppMode,
} from '@databases/modes';
import { type MutableRefObject } from 'react';

const standaloneBridgeApi = new Promise<DatabaseBridgeAPI>((resolve) => {
  if (globalThis.window.databaseBridge) {
    resolve(globalThis.window.databaseBridge);
  } else {
    globalThis.window.addEventListener('load', () => {
      if (globalThis.window.databaseBridge) {
        resolve(globalThis.window.databaseBridge);
      }
    });

    globalThis.window.addEventListener('message', (e) => {
      if (e.source === globalThis.window.parent) {
        const data = e.data;
        if (data && data.type === 'bridge-ready') {
          if (!globalThis.window.databaseBridge) {
            throw new Error('bridge marked as ready but no bridge exists');
          }

          resolve(globalThis.window.databaseBridge);
        }
      }
    });
  }
});

/**
 * @deprecated Use waitForContentAPI from @canvas/frame-api/host-api/host-frame-context instead.
 */
export const getBridgeAPI = async (): Promise<DatabaseBridgeAPI> => {
  if (isStandalone() || isMobile()) {
    return await standaloneBridgeApi;
  } else if (isEmbed() || isNativeEmbed()) {
    return Promise.resolve({
      onboardingService: {
        onOnboardingChange: () => {},
      },
      updateBoardStatus: () => {},
      updateMoreActions: () => {},
      onContentLoadSucceeded:
        (await standaloneBridgeApi).onContentLoadSucceeded || (() => {}),
      onContentLoadFailed: () => {},
      onThemeChange: (await standaloneBridgeApi).onThemeChange || (() => {}),
      onCloseConfluenceSidePanel:
        (await standaloneBridgeApi).onCloseConfluenceSidePanel || (() => {}),
      getVersionName: (await standaloneBridgeApi).getVersionName || (() => undefined),
      objectSidebarMethods: {
        updateObjectSidebarControlState:
          (await standaloneBridgeApi).objectSidebarMethods
            ?.updateObjectSidebarControlState || (() => {}),
        hideObjectSidebarPanel:
          (await standaloneBridgeApi).objectSidebarMethods?.hideObjectSidebarPanel ||
          (() => {}),
        registerChangeObjectSidebarShownHandler:
          (await standaloneBridgeApi).objectSidebarMethods
            ?.registerChangeObjectSidebarShownHandler || (() => {}),
      },
      databaseSpotlightCloseRef:
        (await standaloneBridgeApi).databaseSpotlightCloseRef || undefined,
    });
  } else {
    const error = new Error('Bridge is not available in current mode: ' + getAppMode());
    logError(error);
    throw error;
  }
};

export interface Item {
  id: string;
  text: string;
  shortcutText?: string;
  isDisabled?: boolean;
  showToggle?: {
    isDisabled: boolean;
    isChecked: boolean;
  };
  onClick?: (e: any) => void;
  url?: string;
  target?: string;
}

export interface ItemGroup {
  id: string;
  items: Item[];
  text?: string;
}

export interface MoreActions {
  items: (Item | ItemGroup)[][];
}

type ObjectSidebarBridgeMethods = {
  updateObjectSidebarControlState: (isShown: boolean) => void;
  hideObjectSidebarPanel: () => void;
  registerChangeObjectSidebarShownHandler: (cb: (isShown?: boolean) => void) => void;
};

/**
 * This is the bridge API that is used to communicate between databases and the
 * host Confluence Frontend.
 * See @canvas/frame-api-types for more details.
 *
 * @deprecated Use the Frame API instead.
 */
export interface DatabaseBridgeAPI {
  onboardingService?: DatabaseOnboardingService;
  updateBoardStatus: (data: { status: string; tooltipText: string | undefined }) => void;
  updateMoreActions?: (items: MoreActions) => void;
  onContentLoadSucceeded: (attributes: any) => void;
  onContentLoadFailed: (error: any, attributes: any) => void;
  onThemeChange?: (callback: (theme: Partial<ThemeState>) => void) => void;
  onCloseConfluenceSidePanel?: () => void;
  getVersionName: () => string | undefined;
  objectSidebarMethods?: ObjectSidebarBridgeMethods;
  databaseSpotlightCloseRef?: MutableRefObject<(() => void) | null>;
}

interface DatabaseOnboardingService {
  onOnboardingChange?: (screen: string) => void;
}

declare global {
  interface Window {
    databaseBridge: DatabaseBridgeAPI;
  }
}
